import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Divider, IconButton, MenuItem, Typography } from "@mui/material";
import AMenu from "components/AMenu";
import ClickableText from "components/ClickableText/ClickableText";
import { Comment } from "components/comments/Comment";
import { formatDateTime } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { CommentNotificationType } from "operations/schema/schema";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";

type CommentNotificationProps = {
  commentNotice: CommentNotificationType;
  markAsRead: (notificationId: number) => void;
  onViewDetails: (notification: CommentNotificationType) => void;
};

export const CommentNotification: FC<CommentNotificationProps> = (props) => {
  const { commentNotice, markAsRead, onViewDetails } = props;
  const intl = useIntl();
  const [drawerMenuAnchorEl, setDrawerMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { userVar } = useAppSelector((s) => s.user);
  const canView = useMemo(() => {
    return (
      commentNotice.assignedTechnicianId === userVar?.technicianId ||
      technicianSettings?.advancedPlannerAccess ||
      (technicianSettings.simplePlannerAccess && !commentNotice.assignedTechnicianId)
    );
  }, [commentNotice.assignedTechnicianId, userVar?.technicianId]);

  return (
    <Box sx={{ padding: "0.25em 1em", marginBottom: "1em" }}>
      <Typography variant="caption">{formatDateTime(commentNotice.createdDate)}</Typography>
      <Divider sx={{ margin: "0 -1em 1em -1em" }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "1em",
        }}
      >
        <AccountCircleIcon
          sx={{
            flex: "0 0 auto",
            color: "primary.main",
            width: "24px",
            height: "24px",
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flexGrow: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: "300" }}>
            <FormattedMessage
              id="notifications.mentioned"
              values={{
                sourceIdText: commentNotice.sourceIdText,
                strong: (chunks: any) => <strong>{chunks}</strong>,
              }}
            />
          </Typography>
          <Comment
            commentIn={{
              commentId: commentNotice.commentId,
              fullAccess: false,
              owner: commentNotice.commentOwnerFullName,
              ownerFullName: commentNotice.commentOwnerFullName,
              text: commentNotice.commentText,
              createdDate: commentNotice.commentCreatedDate,
              changedDate: commentNotice.commentChangedDate,
              sourceGuid: commentNotice.sourceGuid,
            }}
          />
          {canView && (
            <ClickableText onClick={() => onViewDetails(commentNotice)}>
              <Typography
                variant="body2"
                sx={{ textDecoration: "underline", color: "primary.main", cursor: "pointer" }}
              >
                <FormattedMessage id="notifications.viewJob" />
              </Typography>
            </ClickableText>
          )}
        </Box>

        {!commentNotice.notificationRead && (
          <>
            <IconButton
              edge="end"
              aria-label={intl.formatMessage({ id: "general.more" })}
              title={intl.formatMessage({ id: "general.more" })}
              onClick={(e) => setDrawerMenuAnchorEl(e.currentTarget)}
              sx={{ flex: "0 0 auto", marginTop: "-6px" }}
            >
              <MoreHorizIcon />
            </IconButton>
          </>
        )}
        {commentNotice.notificationRead && (
          <div
            style={{ flex: "0 0 auto" }}
            title={intl.formatMessage({ id: "notifications.isRead" })}
          >
            <CheckCircleOutlinedIcon />
          </div>
        )}
      </Box>
      <AMenu
        open={Boolean(drawerMenuAnchorEl)}
        anchorEl={drawerMenuAnchorEl}
        onClose={() => setDrawerMenuAnchorEl(null)}
        style={{ zIndex: 1400 }}
      >
        <MenuItem
          onClick={() => {
            markAsRead(commentNotice.notificationId);
            setDrawerMenuAnchorEl(null);
          }}
        >
          <FormattedMessage id="notifications.markAsRead" />
        </MenuItem>
      </AMenu>
    </Box>
  );
};
